import React, { useState } from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import { TranscriptVersion } from '../../repositories/TranscriptRepository';
import { TranscriptEditorStore } from '../TranscriptEditorStore';
import { analytics } from '@src/utility/analytics';

interface VersionOption {
  value: TranscriptVersion;
  label: string;
  description: string;
}

const versionOptions: Record<TranscriptVersion, VersionOption> = {
  [TranscriptVersion.LATEST]: {
    value: TranscriptVersion.LATEST,
    label: 'Latest',
    description: 'Current version with all edits',
  },
  [TranscriptVersion.ORIGINAL]: {
    value: TranscriptVersion.ORIGINAL,
    label: 'Original',
    description: 'Original transcription before any edits',
  },
  [TranscriptVersion.REV]: {
    value: TranscriptVersion.REV,
    label: 'Rev',
    description: 'Initial Rev.ai transcription',
  },
  [TranscriptVersion.AWS]: {
    value: TranscriptVersion.AWS,
    label: 'AWS',
    description: 'Initial AWS transcription',
  },
};

interface VersionSelectorProps {
  editorStore: TranscriptEditorStore;
  onConfirmationRequired: (version: TranscriptVersion) => void;
}

export const TranscriptVersionSelector: React.FC<VersionSelectorProps> = observer(
  ({ editorStore, onConfirmationRequired }) => {
    const [isOpen, setIsOpen] = useState(false);
    const currentVersion = editorStore.currentTranscriptVersion;

    // Filter to available versions
    const availableVersions = editorStore.getAvailableVersions();
    
    // Handle version selection
    const handleVersionSelect = (version: TranscriptVersion) => {
      // Close the dropdown
      setIsOpen(false);
      
      // If selecting the same version, do nothing
      if (version === currentVersion) {
        return;
      }
      
      onConfirmationRequired(version);
    };
    
    return (
      <Container>
        <VersionLabel></VersionLabel>
        <DropdownContainer>
          <CurrentSelection onClick={() => setIsOpen(!isOpen)}>
            {versionOptions[currentVersion]?.label || 'Latest'}
            <DropdownArrow isOpen={isOpen}>▼</DropdownArrow>
          </CurrentSelection>
          
          {isOpen && (
            <OptionsContainer>
              {availableVersions.map((version) => (
                <VersionOption
                  key={version}
                  selected={version === currentVersion}
                  onClick={() => handleVersionSelect(version)}
                >
                  <VersionInfo>
                    <VersionName>{versionOptions[version].label}</VersionName>
                    <VersionDescription>
                      {versionOptions[version].description}
                    </VersionDescription>
                  </VersionInfo>
                </VersionOption>
              ))}
            </OptionsContainer>
          )}
        </DropdownContainer>
      </Container>
    );
  }
);

const Container = styled.div`
  display: flex;
  align-items: center;
  margin-right: 16px;
`;

const VersionLabel = styled.span`
  font-size: 12px;
  color: #a9a9a9;
  margin-right: 8px;
`;

const DropdownContainer = styled.div`
  position: relative;
  min-width: 200px;
`;

const CurrentSelection = styled.div`
  background-color: transparent;
  border: 1px solid #a9a9a9;
  border-radius: 4px;
  color: #f3e9d7;
  padding: 4px 8px;
  font-size: 12px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  &:hover {
    border-color: #f2d093;
  }
`;

const DropdownArrow = styled.span<{ isOpen: boolean }>`
  margin-left: 8px;
  transition: transform 0.2s ease;
  transform: ${(props) => (props.isOpen ? 'rotate(180deg)' : 'rotate(0)')};
  font-size: 8px;
`;

const OptionsContainer = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: #1a1a1a;
  border: 1px solid #484848;
  border-radius: 4px;
  margin-top: 4px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
`;

const VersionOption = styled.div<{ selected: boolean }>`
  padding: 8px;
  cursor: pointer;
  background-color: ${(props) => (props.selected ? '#333' : 'transparent')};
  
  &:hover {
    background-color: #333;
  }
`;

const VersionInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const VersionName = styled.span`
  font-size: 12px;
  color: #f3e9d7;
  font-weight: 600;
`;

const VersionDescription = styled.span`
  font-size: 10px;
  color: #a9a9a9;
  margin-top: 2px;
`;

export default TranscriptVersionSelector;